/**
 * # Eurail Search Form
 */
import { ElementsSDK } from '@yiluhub/ui-sdk-react';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { yiluEnv } from 'utils';
import { getCurrentLanguage } from 'utils/i18n';

import { getLocalizedLocationData } from '../../../../utils/locationData';
import { getFareOption, getLocationOption, reorderLocations } from '../../../../utils/selectUtils';
import { FareType, PassType, getFareOptions } from '../../../../utils/types';
import './styles.scss';
import { EurailSearchFormProps } from './types';
import { useEurailSipSearchForm } from './useEurailSipSearchForm';

export const EurailSipSearchForm: React.FC<EurailSearchFormProps> = ({ searchInput, onSubmit }) => {
  const { t } = useTranslation();
  const currentLanguage = getCurrentLanguage();

  const {
    travellersFields,
    handleOnSubmitButtonClick,
    isFormValid,
    pass,
    setPass,
    to,
    setTo,
    fareClass,
    setFareClass,
  } = useEurailSipSearchForm({
    searchInput,
    onSubmit,
  });

  const isGlobalPass = pass === PassType.GLOBAL;

  const tabOptions = [
    { label: t('eurail.pass.global'), value: PassType.GLOBAL },
    { label: t('eurail.pass.one_country'), value: PassType.ONE_COUNTRY },
  ];
  const { YILU_EURAIL_COUNTRY_PRIORITY_LIST } = yiluEnv.getVariables();
  const fareOptions = getFareOptions(t);
  const prioritizedCountryList = reorderLocations(
    getLocalizedLocationData(currentLanguage),
    YILU_EURAIL_COUNTRY_PRIORITY_LIST,
  );

  return (
    <div className="eurail-Sip_SearchForm">
      <ElementsSDK.TabSelector
        className="eurail-Sip_SearchForm__TabSelector"
        options={tabOptions}
        selectedValue={pass ?? PassType.GLOBAL}
        onChange={(value: string) => setPass && setPass(value as PassType)}
        dataTestId="eurail-pass-type-selector"
      />
      <div className="eurail-Sip_SearchForm__Content">
        <ElementsSDK.Typography variant="h6" uppercase className="eurail-Sip_SearchForm__Title">
          {t(`eurail.${isGlobalPass ? 'global' : 'local'}.form.title`)}
        </ElementsSDK.Typography>
        <ElementsSDK.Typography variant="p1" className="eurail-Sip_SearchForm__Description">
          {t(`eurail.${isGlobalPass ? 'global' : 'local'}.form.description`)}
        </ElementsSDK.Typography>
        <div className="eurail-Sip_SearchForm__Row">
          <ElementsSDK.InputField.Select
            dataTestId="eurail-pass-location-selector"
            label={t('eurail.location_label')}
            placeholder={t('eurail.location_placeholder')}
            className={clsx(
              'eurail-Sip_SearchForm__Row',
              isGlobalPass && 'eurail-Sip_SearchForm__Row--hidden',
            )}
            defaultOption={getLocationOption(to, currentLanguage)}
            options={prioritizedCountryList}
            onSelect={(value) => setTo(value as string)}
            icon={<ElementsSDK.Icon.PinOutline />}
          />
        </div>
        <div
          className={clsx(
            'eurail-Sip_SearchForm__Row',
            !isGlobalPass && 'eurail-Sip_SearchForm__Row--inline',
          )}
        >
          <ElementsSDK.TravellersSelector
            dataTestId="eurail-travellers-selector"
            countLabelSingular={t('Traveller')}
            countLabelPlural={t('Travellers')}
            label={t('Travellers')}
            travellers={travellersFields}
            className="eurail-Sip_SearchForm__Field"
          />
          <ElementsSDK.InputField.Select
            dataTestId="eurail-fare-selector"
            label={t('eurail.class_label')}
            options={fareOptions}
            defaultOption={getFareOption(fareClass as FareType, fareOptions)}
            className="eurail-Sip_SearchForm__Field"
            onSelect={(value) => setFareClass(value as string)}
          />
        </div>

        <ElementsSDK.Button
          aria-label={t('Search')}
          className="eurail-Sip_SearchForm__Button"
          disabled={!isFormValid}
          onClick={handleOnSubmitButtonClick}
        >
          {t('Search')}
        </ElementsSDK.Button>
      </div>
    </div>
  );
};
